import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { fetchAvailableFilters, fetchProjects } from "@/stores/documents/repository/metadata";
import {
  applySignedContainerRowRule,
  fetchUnzippedSignedContainerIds,
} from "@/stores/documents/repository/queries";
import { SUPPORTED_FILE_EXTENSIONS } from "@/domains/documents/constants/fileTypes";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { Language } from "@/lib/i18n/types";

// Label resolvers — mirror the fallback chain used in DocumentListTable so that
// dashboard chart labels stay consistent with the document list.
function pickCategoryLabel(c: { name: string; name_et: string | null }, lang: Language): string {
  if (lang === "et") return c.name_et?.trim() || c.name;
  return c.name;
}

function pickDisciplineLabel(
  d: { name: string; name_en: string | null; description: string | null },
  lang: Language
): string {
  if (lang === "en") return d.name_en?.trim() || d.description?.trim() || d.name;
  return d.name;
}

function pickStageLabel(s: { name: string; name_en: string | null }, lang: Language): string {
  if (lang === "en") return s.name_en?.trim() || s.name;
  return s.name;
}

export type GroupBy = "category" | "discipline" | "stage";
export type TimeRange = "30d" | "90d" | "1y";

export interface GroupCount {
  name: string;
  count: number;
}

export interface UploadPeriod {
  period: string;
  count: number;
}

export interface UserUpload {
  userId: number;
  firstName: string;
  lastName: string;
  /** Email is shown in the tooltip when both first and last name are blank,
   *  so users without a populated profile name still have something readable. */
  email: string | null;
  /** Storage path in the `avatars` bucket or a fully-qualified OAuth URL —
   *  resolved by `resolveAvatarUrl`. Null when the user has no picture, in
   *  which case the tooltip avatar falls back to initials. */
  avatarUrl: string | null;
  /** Stored avatar fill colour (hex), used for the initials fallback. */
  color: string | null;
  count: number;
}

export interface ProjectCount {
  projectId: number;
  projectName: string;
  count: number;
}

export interface ProjectCategoryBreakdown {
  projectId: number;
  categoryName: string;
  count: number;
}

// States that count as "successfully processed" for the dashboard
// "Processed" tile. IFC_PROCESSED is the terminal success state for IFC docs
// (no FINALIZING step, no child documents — see documentState.ts).
const PROCESSED_STATES = [
  "PROCESSED",
  "FOLDERED",
  "TAGGED",
  "STAGED",
  "EMBEDDED",
  "COMPLETE",
  "IFC_PROCESSED",
  "UNZIPPED",
];

// PostgREST OR-clause matching any filename ending in a Franklin-supported extension.
const SUPPORTED_FILENAME_OR = SUPPORTED_FILE_EXTENSIONS.map(
  (ext) => `filename.ilike.*.${ext}`
).join(",");

const PAGE_SIZE = 1000;

// Paginate any Supabase select builder past PostgREST's default 1000-row cap.
// Same pattern as fetchCustomTags in src/stores/documents/repository/metadata.ts.
async function fetchAllPages<T>(build: () => any): Promise<T[]> {
  const out: T[] = [];
  let from = 0;
  while (true) {
    const { data, error } = await build().range(from, from + PAGE_SIZE - 1);
    if (error) throw error;
    const page = (data ?? []) as T[];
    out.push(...page);
    if (page.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }
  return out;
}

function getDateCutoff(timeRange: TimeRange): string {
  const now = new Date();
  switch (timeRange) {
    case "30d":
      now.setDate(now.getDate() - 30);
      break;
    case "90d":
      now.setDate(now.getDate() - 90);
      break;
    case "1y":
      now.setFullYear(now.getFullYear() - 1);
      break;
  }
  return now.toISOString();
}

function bucketByPeriod(rows: Array<{ created_at: string }>, timeRange: TimeRange): UploadPeriod[] {
  const map = new Map<string, number>();

  for (const row of rows) {
    const date = new Date(row.created_at);
    let key: string;
    if (timeRange === "1y") {
      // Weekly bucket: snap to Monday of that week
      const day = date.getUTCDay();
      const monday = new Date(date);
      monday.setUTCDate(date.getUTCDate() - ((day + 6) % 7));
      key = monday.toISOString().slice(0, 10); // YYYY-MM-DD (Monday)
    } else {
      key = date.toISOString().slice(0, 10); // YYYY-MM-DD
    }
    map.set(key, (map.get(key) ?? 0) + 1);
  }

  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([period, count]) => ({ period, count }));
}

function bucketByPeriodAndUser(
  rows: Array<{
    created_at: string;
    created_by: number;
    user_profiles: {
      id: number;
      first_name: string | null;
      last_name: string | null;
      email: string | null;
      avatar_url: string | null;
      color: string | null;
    } | null;
  }>,
  timeRange: TimeRange
): Map<string, UserUpload[]> {
  const map = new Map<string, Map<number, UserUpload>>();

  for (const row of rows) {
    const date = new Date(row.created_at);
    let periodKey: string;
    if (timeRange === "1y") {
      const day = date.getUTCDay();
      const monday = new Date(date);
      monday.setUTCDate(date.getUTCDate() - ((day + 6) % 7));
      periodKey = monday.toISOString().slice(0, 10);
    } else {
      periodKey = date.toISOString().slice(0, 10);
    }

    if (!map.has(periodKey)) map.set(periodKey, new Map());
    const users = map.get(periodKey)!;

    const userId = row.created_by;
    const existing = users.get(userId);
    if (existing) {
      existing.count++;
    } else {
      users.set(userId, {
        userId,
        firstName: row.user_profiles?.first_name ?? "",
        lastName: row.user_profiles?.last_name ?? "",
        email: row.user_profiles?.email ?? null,
        avatarUrl: row.user_profiles?.avatar_url ?? null,
        color: row.user_profiles?.color ?? null,
        count: 1,
      });
    }
  }

  const result = new Map<string, UserUpload[]>();
  for (const [period, users] of map) {
    result.set(
      period,
      Array.from(users.values()).sort((a, b) => b.count - a.count)
    );
  }
  return result;
}

// --- Query: Total + Processed counts ---
function useTotalProcessedCount(buildingId: string | undefined, projectId?: string) {
  return useQuery({
    // projectId is part of the key so scoped + unscoped counts never collide in
    // the shared cache (CLAUDE.md: a shared queryKey is a shared cache entry).
    queryKey: ["building-doc-stats", "counts", buildingId, projectId ?? null],
    queryFn: async () => {
      const buildingIdNum = parseInt(buildingId!, 10);
      const projectIdNum = projectId ? Number(projectId) : null;
      const signedContainerIds = await fetchUnzippedSignedContainerIds(buildingIdNum);

      let totalQ = supabase
        .from("documents")
        .select("id", { count: "exact", head: true })
        .eq("building_id", buildingIdNum)
        .eq("is_deleted", false)
        .eq("is_latest_version", true)
        .eq("hidden", false)
        .or("is_dwg_child.is.null,is_dwg_child.eq.false")
        .or(SUPPORTED_FILENAME_OR);
      let processedQ = supabase
        .from("documents")
        .select("id", { count: "exact", head: true })
        .eq("building_id", buildingIdNum)
        .eq("is_deleted", false)
        .eq("is_latest_version", true)
        .eq("hidden", false)
        .or("is_dwg_child.is.null,is_dwg_child.eq.false")
        .or(SUPPORTED_FILENAME_OR)
        .in("state", PROCESSED_STATES);
      totalQ = applySignedContainerRowRule(totalQ, signedContainerIds);
      processedQ = applySignedContainerRowRule(processedQ, signedContainerIds);
      if (projectIdNum != null) {
        totalQ = totalQ.eq("project_id", projectIdNum);
        processedQ = processedQ.eq("project_id", projectIdNum);
      }

      const [totalRes, processedRes] = await Promise.all([totalQ, processedQ]);

      if (totalRes.error) throw totalRes.error;
      if (processedRes.error) throw processedRes.error;

      return {
        total: totalRes.count ?? 0,
        processed: processedRes.count ?? 0,
      };
    },
    enabled: !!buildingId,
    staleTime: 5 * 60 * 1000,
  });
}

// --- Query: Available filters (reuses the /documents page aggregation) ---
// Returns per-category, per-discipline, per-stage, and per-project counts in
// one paginated pass. Source of truth is `fetchAvailableFilters` in
// src/stores/documents/repository/metadata.ts — the same function that powers
// the /documents filter sidebar.
function useBuildingFilters(buildingId: string | undefined, projectId?: string) {
  return useQuery({
    queryKey: ["building-doc-stats", "available-filters", buildingId, projectId ?? null],
    queryFn: () => fetchAvailableFilters(buildingId!, projectId),
    enabled: !!buildingId,
    staleTime: 5 * 60 * 1000,
  });
}

// --- Query: Building projects (for mapping project IDs → names) ---
function useBuildingProjects(buildingId: string | undefined) {
  return useQuery({
    queryKey: ["building-doc-stats", "projects", buildingId],
    queryFn: () => fetchProjects(buildingId!),
    enabled: !!buildingId,
    staleTime: 5 * 60 * 1000,
  });
}

/** Shortest range (30d → 90d → 1y) whose window contains the given upload
 * date. Falls through to "1y" for older uploads (the chart then shows its
 * empty state without a misleading "try a longer range" hint) and to "30d"
 * when the building has no uploads at all. */
export function shortestRangeContaining(latestUploadAt: string | null | undefined): TimeRange {
  if (!latestUploadAt) return "30d";
  const latest = new Date(latestUploadAt).getTime();
  if (Number.isNaN(latest)) return "30d";
  if (latest >= new Date(getDateCutoff("30d")).getTime()) return "30d";
  if (latest >= new Date(getDateCutoff("90d")).getTime()) return "90d";
  return "1y";
}

// --- Query: Latest upload date (drives the default Upload Activity range) ---
// Same visibility filters as useUploadActivity so "has data in range" agrees
// with what the chart would actually plot.
export function useLatestUploadDate(buildingId: string | undefined, projectId?: string) {
  return useQuery({
    queryKey: ["building-doc-stats", "latest-upload", buildingId, projectId ?? null],
    queryFn: async (): Promise<string | null> => {
      const buildingIdNum = parseInt(buildingId!, 10);
      const projectIdNum = projectId ? Number(projectId) : null;

      let q = supabase
        .from("documents")
        .select("created_at")
        .eq("building_id", buildingIdNum)
        .eq("is_deleted", false)
        .eq("hidden", false);
      if (projectIdNum != null) q = q.eq("project_id", projectIdNum);
      const { data, error } = await q.order("created_at", { ascending: false }).limit(1);

      if (error) throw error;
      return data?.[0]?.created_at ?? null;
    },
    enabled: !!buildingId,
    staleTime: 5 * 60 * 1000,
  });
}

// --- Query: Upload activity over time ---
function useUploadActivity(
  buildingId: string | undefined,
  timeRange: TimeRange,
  projectId?: string
) {
  return useQuery({
    queryKey: ["building-doc-stats", "upload-activity", buildingId, timeRange, projectId ?? null],
    queryFn: async () => {
      const buildingIdNum = parseInt(buildingId!, 10);
      const projectIdNum = projectId ? Number(projectId) : null;
      const cutoff = getDateCutoff(timeRange);

      const rows = await fetchAllPages<{ created_at: string }>(() => {
        let q = supabase
          .from("documents")
          .select("created_at")
          .eq("building_id", buildingIdNum)
          .eq("is_deleted", false)
          .eq("hidden", false)
          .gte("created_at", cutoff);
        if (projectIdNum != null) q = q.eq("project_id", projectIdNum);
        return q.order("created_at", { ascending: true });
      });

      return bucketByPeriod(rows, timeRange);
    },
    enabled: !!buildingId,
    staleTime: 5 * 60 * 1000,
  });
}

// --- Query: Upload activity by user per period ---
function useUploadActivityByUser(
  buildingId: string | undefined,
  timeRange: TimeRange,
  projectId?: string
) {
  return useQuery({
    queryKey: ["building-doc-stats", "upload-by-user", buildingId, timeRange, projectId ?? null],
    queryFn: async () => {
      const buildingIdNum = parseInt(buildingId!, 10);
      const projectIdNum = projectId ? Number(projectId) : null;
      const cutoff = getDateCutoff(timeRange);

      const rows = await fetchAllPages<any>(() => {
        let q = supabase
          .from("documents")
          .select(
            "created_at, created_by, user_profiles!documents_created_by_fk(id, first_name, last_name, email, avatar_url, color)"
          )
          .eq("building_id", buildingIdNum)
          .eq("is_deleted", false)
          .eq("hidden", false)
          .gte("created_at", cutoff);
        if (projectIdNum != null) q = q.eq("project_id", projectIdNum);
        return q.order("created_at", { ascending: true });
      });

      return bucketByPeriodAndUser(rows, timeRange);
    },
    enabled: !!buildingId,
    staleTime: 5 * 60 * 1000,
  });
}

// --- Query: Per-project category breakdown ---
// Fires one fetchAvailableFilters call per project and collates categories.
// Reuses the /documents aggregation, so per-project counts match exactly what
// the documents page shows when filtering by the same project.
function useProjectCategoryBreakdown(
  buildingId: string | undefined,
  projectIds: number[],
  language: Language
) {
  return useQuery({
    queryKey: ["building-doc-stats", "project-categories", buildingId, projectIds, language],
    queryFn: async () => {
      const results = await Promise.all(
        projectIds.map((pid) =>
          fetchAvailableFilters(buildingId!, String(pid)).then((f) => ({ pid, f }))
        )
      );
      const out = new Map<number, ProjectCategoryBreakdown[]>();
      for (const { pid, f } of results) {
        out.set(
          pid,
          f.categories
            .filter((c) => c.name !== "Other")
            .map((c) => ({
              projectId: pid,
              categoryName: pickCategoryLabel(c, language),
              count: c.count,
            }))
            .sort((a, b) => b.count - a.count)
        );
      }
      return out;
    },
    enabled: !!buildingId && projectIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });
}

// --- Main hook ---
export function useBuildingDocumentStats(
  buildingId: string | undefined,
  options: { timeRange: TimeRange; projectId?: string }
) {
  const { language } = useLanguage();
  const { projectId } = options;
  const counts = useTotalProcessedCount(buildingId, projectId);
  const filters = useBuildingFilters(buildingId, projectId);
  const projects = useBuildingProjects(buildingId);
  const uploadActivity = useUploadActivity(buildingId, options.timeRange, projectId);
  const uploadByUser = useUploadActivityByUser(buildingId, options.timeRange, projectId);

  // Per-project list (derived from the filters response + project names)
  const projectNameById = new Map<number, string>();
  for (const p of projects.data ?? []) {
    projectNameById.set(Number(p.id), p.name);
  }

  const perProject: ProjectCount[] = (filters.data?.projects ?? [])
    .map((p) => ({
      projectId: p.id,
      projectName: projectNameById.get(p.id) ?? "Unknown",
      count: p.count,
    }))
    .sort((a, b) => b.count - a.count);

  const perProjectIds = perProject.map((p) => p.projectId);
  const projectCategories = useProjectCategoryBreakdown(buildingId, perProjectIds, language);

  // Donut chart data — all three groupings at once, projected from the one
  // cached filters response (the dashboard shows category, discipline and
  // stage side by side, so splitting them costs nothing extra). Filter
  // "Other" against the canonical `name` before translating so the filter
  // does not drift across languages.
  const byGroup: Record<GroupBy, GroupCount[]> = {
    category: (filters.data?.categories ?? [])
      .filter((c) => c.name !== "Other")
      .map((c) => ({ name: pickCategoryLabel(c, language), count: c.count }))
      .sort((a, b) => b.count - a.count),
    discipline: (filters.data?.designDisciplines ?? [])
      .map((d) => ({ name: pickDisciplineLabel(d, language), count: d.count }))
      .sort((a, b) => b.count - a.count),
    stage: (filters.data?.designStages ?? [])
      .map((s) => ({ name: pickStageLabel(s, language), count: s.count }))
      .sort((a, b) => b.count - a.count),
  };

  return {
    // Counts
    total: counts.data?.total ?? 0,
    processed: counts.data?.processed ?? 0,
    isCountsLoading: counts.isLoading,

    // By group (donut chart)
    byGroup,
    isByGroupLoading: filters.isLoading,

    // Upload activity (line chart)
    uploadActivity: uploadActivity.data ?? [],
    isUploadActivityLoading: uploadActivity.isLoading,

    // Upload activity by user (line chart tooltip)
    uploadByUser: uploadByUser.data ?? new Map<string, UserUpload[]>(),

    // Per project (bar chart)
    perProject,
    isPerProjectLoading: filters.isLoading || projects.isLoading,

    // Per project category breakdown (bar chart tooltip)
    projectCategories: projectCategories.data ?? new Map<number, ProjectCategoryBreakdown[]>(),
  };
}
